<template>
  <div class="fair-survey-step">
    <div class="fair-survey-step__head">
      <h2 class="fair-survey-step__title">
        {{ step.title }}
      </h2>
      <p
        v-if="step.subtitle"
        class="fair-survey-step__subtitle"
      >
        {{ step.subtitle }}
      </p>
    </div>

    <div class="gift-step__fields">
      <div class="gift-step__field">
        <span class="gift-step__label">Валюта</span>
        <FairDropdown
          v-model="selectedCurrency"
          :options="step.currencyOptions.map((option) => ({
            key: option.value,
            label: option.label,
          }))"
          placeholder="Выберите валюту"
          show-radio
          @update:model-value="updateCurrency"
        />
      </div>

      <!-- Ranges depend on the currency, so they appear only once it is set. -->
      <div
        v-if="selectedCurrency"
        class="gift-step__field"
      >
        <span class="gift-step__label">Ценовой диапазон</span>
        <div class="fair-survey-step__options">
          <button
            v-for="option in currentPriceRanges"
            :key="option.value"
            type="button"
            class="fair-survey-step__option"
            :class="{
              'fair-survey-step__option--selected':
                selectedPriceRange === option.value,
            }"
            :aria-pressed="selectedPriceRange === option.value"
            @click="pickPriceRange(option.value)"
          >
            <FairRadioButtonIndicator :selected="selectedPriceRange === option.value" />
            <span class="fair-survey-step__option-label">{{ option.label }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { FairDropdown } from '@/ui/primitives/FairDropdown'
import { FairRadioButtonIndicator } from '@/ui/primitives/FairRadioButton'
import { useGiftModal } from '@/features/gift/store'
import type { GiftStep, PriceRangeOption } from '@/features/gift/types/gift-config'

interface Props {
  step: GiftStep & { type: 'price_range' }
}

const props = defineProps<Props>()
const giftStore = useGiftModal()

const selectedCurrency = ref<string | null>(null)
const selectedPriceRange = ref<string | null>(null)

// Вычисляем доступные ценовые диапазоны для выбранной валюты
const currentPriceRanges = computed<PriceRangeOption[]>(() => {
  if (!selectedCurrency.value) {
    return []
  }
  return props.step.priceRangesByCurrency[selectedCurrency.value] || []
})

function updateCurrency() {
  giftStore.updateGiftData(`${props.step.id}_currency`, selectedCurrency.value)
  // При смене валюты сбрасываем выбранный ценовой диапазон
  selectedPriceRange.value = null
  giftStore.updateGiftData(`${props.step.id}_priceRange`, null)
}

function pickPriceRange(value: string) {
  selectedPriceRange.value = value
  updatePriceRange()
}

function updatePriceRange() {
  giftStore.updateGiftData(`${props.step.id}_priceRange`, selectedPriceRange.value)
}

onMounted(() => {
  const data = giftStore.giftModal.giftData
  selectedCurrency.value = data[`${props.step.id}_currency`] || null
  selectedPriceRange.value = data[`${props.step.id}_priceRange`] || null
})
</script>

<style scoped>
.gift-step__fields {
  display: flex;
  flex-direction: column;
  gap: var(--space-24);
}

.gift-step__field {
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
}

.gift-step__label {
  font-family: var(--font-display);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--color-text-muted);
}

/* Sub-question belongs to the option above it, so it is indented under it. */
.gift-step__sub {
  padding: var(--space-8) 0 var(--space-8)
    var(--fair-rd-space-40);
}
</style>
